/*
 * sn-header.js — en-tête SN (JS vanilla, aucune dépendance)
 * - <sn-header> : publie la hauteur de l'en-tête dans la variable CSS --header-height
 *   (utilisée par Dawn et par les colonnes collantes de la fiche produit).
 * - <sn-menu-drawer> : tiroir de menu (ouverture/fermeture, voile, Échap, piège du focus,
 *   blocage du défilement de la page, retour du focus sur le bouton).
 */
(function () {
  'use strict';

  if (!customElements.get('sn-header')) {
    class SnHeader extends HTMLElement {
      connectedCallback() {
        this.update = () => {
          document.documentElement.style.setProperty('--header-height', this.offsetHeight + 'px');
        };
        this.update();
        if ('ResizeObserver' in window) {
          this.observer = new ResizeObserver(this.update);
          this.observer.observe(this);
        } else {
          window.addEventListener('resize', this.update);
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        window.removeEventListener('resize', this.update);
      }
    }
    customElements.define('sn-header', SnHeader);
  }

  if (!customElements.get('sn-menu-drawer')) {
    const FOCUSABLE =
      'a[href], button:not([disabled]), summary, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    class SnMenuDrawer extends HTMLElement {
      connectedCallback() {
        this.panel = this.querySelector('.sn-drawer__panel');
        this.section = this.closest('.shopify-section');

        this.onDocumentClick = (event) => {
          const toggle = event.target.closest('[aria-controls="' + this.id + '"]');
          if (!toggle || this.contains(toggle)) return;
          event.preventDefault();
          this.isOpen() ? this.close() : this.open(toggle);
        };

        this.onKeydown = (event) => {
          if (!this.isOpen()) return;
          if (event.key === 'Escape') {
            event.preventDefault();
            this.close();
            return;
          }
          if (event.key === 'Tab') this.trapFocus(event);
        };

        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeydown);
        this.querySelectorAll('[data-sn-drawer-close]').forEach((element) => {
          element.addEventListener('click', () => this.close());
        });
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeydown);
        if (this.isOpen()) document.body.classList.remove('overflow-hidden');
      }

      isOpen() {
        return this.classList.contains('is-open');
      }

      open(trigger) {
        this.trigger = trigger || document.querySelector('[aria-controls="' + this.id + '"]');
        if (this.section) this.section.classList.add('sn-menu-is-open');
        this.classList.add('is-open');
        if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
        document.body.classList.add('overflow-hidden');
        const firstFocusable = this.panel.querySelector('.sn-drawer__close') || this.panel;
        window.setTimeout(() => firstFocusable.focus({ preventScroll: true }), 50);
      }

      close() {
        if (!this.isOpen()) return;
        this.classList.remove('is-open');
        if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('overflow-hidden');
        window.setTimeout(() => {
          if (!this.isOpen() && this.section) this.section.classList.remove('sn-menu-is-open');
        }, 300);
        if (this.trigger) this.trigger.focus({ preventScroll: true });
      }

      trapFocus(event) {
        const focusables = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter(
          (element) => element.offsetParent !== null || element === document.activeElement
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
    customElements.define('sn-menu-drawer', SnMenuDrawer);
  }
})();
